import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { CHARACTER_REGION_LABELS, type CharacterSettings, type CharacterSummary } from '@thottbot/shared';
import { apiGet, apiSend } from '../../api/client';
import { relativeTime } from '../../util/time';
import { describeError } from '../Submit';

interface AdminCharacter extends CharacterSummary {
  ownerBattletag: string;
  ownerId: string;
  guild: string | null;
}
interface Totals {
  total: number;
  publicCount: number;
  failed: number;
}

const PAGE = 50;
const NAMESPACE_RE = /^[a-z0-9]{2,32}$/;

export function Characters() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [namespace, setNamespace] = useState('');
  const [label, setLabel] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['admin', 'characters', 'settings'],
    queryFn: () => apiGet<CharacterSettings>('/api/admin/characters/settings'),
  });
  useEffect(() => {
    if (settings.data) {
      setNamespace(settings.data.namespace);
      setLabel(settings.data.label);
    }
  }, [settings.data]);
  const totals = useQuery({
    queryKey: ['admin', 'characters', 'totals'],
    queryFn: () => apiGet<Totals>('/api/admin/characters/totals'),
  });
  const list = useQuery({
    queryKey: ['admin', 'characters', 'list', page, search],
    queryFn: () =>
      apiGet<{ page: number; total: number; items: AdminCharacter[] }>(
        `/api/admin/characters?page=${page}${search ? `&q=${encodeURIComponent(search)}` : ''}`,
      ),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'characters'] });
    qc.invalidateQueries({ queryKey: ['me', 'characters'] });
  };

  const save = useMutation({
    mutationFn: (body: CharacterSettings) => apiSend<CharacterSettings>('PUT', '/api/admin/characters/settings', body),
    onSuccess: () => {
      setError(null);
      setSaved('Character settings saved');
      invalidate();
    },
    onError: (err) => setError(describeError(err)),
  });
  const hide = useMutation({
    mutationFn: (id: string) => apiSend<CharacterSummary>('PATCH', `/api/admin/characters/${id}`, { public: false }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/api/admin/characters/${id}`),
    onSuccess: () => {
      setError(null);
      setConfirming(null);
      invalidate();
    },
    onError: (err) => setError(describeError(err)),
  });

  const valid = NAMESPACE_RE.test(namespace.trim()) && label.trim().length > 0 && label.trim().length <= 40;

  return (
    <div data-testid="admin-characters">
      <form
        className="section"
        aria-label="Character settings"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ namespace: namespace.trim(), label: label.trim() });
        }}
      >
        <div className="form-row">
          <label htmlFor="characters-namespace">Namespace</label>
          <input
            id="characters-namespace"
            type="text"
            value={namespace}
            pattern="[a-z0-9]{2,32}"
            title="The token between profile- and -region in Blizzard's namespace (classic1x for Classic Era)"
            onChange={(e) => setNamespace(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="characters-label">Label</label>
          <input id="characters-label" type="text" value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="actions">
          <button type="submit" disabled={save.isPending || !valid}>
            Save
          </button>
          <span className="muted">
            Imports read Blizzard's <code>profile-&lt;namespace&gt;-&lt;region&gt;</code>; the label is what players see
            ("Imports from ...").
          </span>
        </div>
        {saved && (
          <div className="notice" role="status">
            {saved}
          </div>
        )}
      </form>
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
      {totals.data && (
        <p data-testid="character-totals">
          {totals.data.total} imported, {totals.data.publicCount} public, {totals.data.failed} failed
        </p>
      )}
      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q.trim());
          setPage(1);
        }}
      >
        <label htmlFor="characters-search">Search</label>{' '}
        <input id="characters-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, realm or BattleTag" />{' '}
        <button type="submit">Search</button>
      </form>
      {list.isPending && <p className="muted">Loading...</p>}
      {list.data && (
        <>
          <div className="table-scroll">
            <table className="grid" data-testid="admin-characters-table">
              <thead>
                <tr>
                  <th scope="col">Owner</th>
                  <th scope="col">Name</th>
                  <th scope="col">Realm</th>
                  <th scope="col">Level</th>
                  <th scope="col">Class</th>
                  <th scope="col">Status</th>
                  <th scope="col">Public</th>
                  <th scope="col">Imported</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((c) => (
                  <tr key={c.id} data-testid="admin-character-row" data-public={c.public} data-status={c.detailStatus}>
                    <td>{c.ownerBattletag}</td>
                    <td>
                      <Link to={c.url}>{c.name}</Link>
                    </td>
                    <td>
                      {c.realmName} ({CHARACTER_REGION_LABELS[c.region]})
                    </td>
                    <td>{c.level}</td>
                    <td>{c.class}</td>
                    <td className={c.detailStatus === 'failed' ? 'error' : ''}>
                      {c.detailStatus}
                      {c.error && <span className="muted"> - {c.error}</span>}
                    </td>
                    <td data-testid="admin-character-public">{c.public ? 'yes' : 'no'}</td>
                    <td>{relativeTime(c.importedAt)}</td>
                    <td>
                      {c.public && (
                        <button type="button" onClick={() => hide.mutate(c.id)} disabled={hide.isPending}>
                          Hide
                        </button>
                      )}{' '}
                      {confirming === c.id ? (
                        <>
                          <button type="button" onClick={() => remove.mutate(c.id)} disabled={remove.isPending}>
                            Confirm delete
                          </button>{' '}
                          <button type="button" onClick={() => setConfirming(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => setConfirming(c.id)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {Math.max(1, Math.ceil(list.data.total / PAGE))}
            </span>
            <button type="button" disabled={page * PAGE >= list.data.total} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
